import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Mark } from "../components/Mark";
import { Button, InlineNote, Notice, buttonClasses } from "../components/primitives";
import { ApiError, api } from "../lib/api";
import { BRAND } from "../lib/brand";
import type { LinkCheck, LinkRefusal } from "../lib/types";

/** The token from `#token=…`. Read once: the address bar drops it at once. */
function tokenFromHash(): string {
  const hash = window.location.hash.replace(/^#/, "");
  return new URLSearchParams(hash).get("token") ?? "";
}

function refused(value: object | undefined): value is LinkRefusal {
  return !!value && "code" in value;
}

/**
 * Where every emailed sign-in link opens.
 *
 * **Opening it spends nothing; the button does.** A link used to sign in on
 * the GET, and a GET is what a mail scanner, a link preview and the phone that
 * opened the mail first all send. On the beta one corporate address had each
 * link spent seconds after it arrived, and its owner's own click read "already
 * used" — twice, and they never got in. The token rides in the fragment, so no
 * server ever sees it by the page being opened.
 *
 * **Every state says what to do next.** Used and expired offer a new link to
 * the same address with one press; a link that never existed sends you back to
 * ask with an address; a second press in the browser it already signed in just
 * continues. The page branches on the server's `code`, never on its prose.
 */
export function SignIn() {
  const [token] = useState(tokenFromHash);

  // Out of the address bar and the history at once: the token is a bearer
  // credential until it is spent, and a URL is what gets copied and shared.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
    // A second link opened in a tab already on this page changes only the
    // fragment, which loads nothing: the page would go on showing the first
    // link's state with the new token sitting in the address bar. Start over.
    const restart = () => {
      if (tokenFromHash()) window.location.reload();
    };
    window.addEventListener("hashchange", restart);
    return () => window.removeEventListener("hashchange", restart);
  }, []);

  const check = useQuery({
    queryKey: ["login-link", token],
    queryFn: () => api.checkLoginLink(token),
    enabled: token !== "",
    retry: false,
    // A link's state changes only by being used here; refetching on focus
    // would re-ask about a token this page has already spent.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const redeem = useMutation({
    mutationFn: () => api.redeemLoginLink(token),
    onSuccess: (result) => {
      // A full navigation, not the router: the session is new, and every
      // cached answer on this page was given to somebody signed out.
      if (!refused(result)) window.location.replace(result.next);
    },
  });

  const resend = useMutation({ mutationFn: () => api.resendLoginLink(token) });

  const checked = check.data;
  const lookup: LinkCheck | null = checked && !("code" in checked) ? checked : null;
  const failure = refused(redeem.data) ? redeem.data : null;
  // What to draw: a refusal from pressing the button outranks the check made
  // before it, which a scanner may have overtaken since.
  const state = !token
    ? "invalid"
    : failure
      ? failure.code
      : lookup?.signed_in
        ? "signed-in"
        : (lookup?.state ?? null);
  const email = failure?.email ?? lookup?.email ?? null;

  if (token && check.isLoading) {
    return (
      <Frame title="Checking your link…">
        <p className="sr-only">Checking the sign-in link</p>
      </Frame>
    );
  }

  if (check.isError) {
    return (
      <Frame title="We could not check this link">
        <p>{(check.error as Error).message}</p>
        <BackToSignIn />
      </Frame>
    );
  }

  if (state === "ready" || state === "signed-in") {
    return (
      <Frame title={state === "ready" ? `Sign in to ${BRAND.name}` : "You are already signed in"}>
        <p>
          as <span className="font-medium text-ink">{email}</span>
        </p>
        <Button
          variant="primary"
          className="mt-7 w-full"
          autoFocus
          disabled={redeem.isPending || redeem.isSuccess}
          onClick={() => redeem.mutate()}
        >
          {redeem.isPending || redeem.isSuccess ? "Signing in…" : "Continue"}
        </Button>
        {redeem.isError && (
          <InlineNote tone="critical" className="mt-3 justify-center">
            {(redeem.error as Error).message}
          </InlineNote>
        )}
        {state === "ready" && (
          <p className="mt-5 text-xs text-muted">
            The link works once. Nothing is signed in until you press Continue.
          </p>
        )}
      </Frame>
    );
  }

  if (state === "used" || state === "expired") {
    const sent = resend.data;
    return (
      <Frame
        title={
          state === "used" ? "This link has already been used" : "This link has expired"
        }
      >
        <p>
          {state === "used"
            ? "Each link signs in once. If that was not you, something else opened it first — a mail security scanner, or the same mail on another device."
            : "A sign-in link works for 15 minutes after it is sent."}
        </p>
        {resend.isSuccess && !refused(sent) ? (
          <Notice tone="neutral" title="A new link is on its way" className="mt-7 text-left">
            <p className="text-ink-2">
              Sent to <span className="font-medium text-ink">{sent?.email}</span>.
              Open the newest one — it is good for 15 minutes.
            </p>
          </Notice>
        ) : (
          <>
            <Button
              variant="primary"
              className="mt-7 w-full"
              autoFocus
              disabled={resend.isPending || !email}
              onClick={() => resend.mutate()}
            >
              {resend.isPending ? "Sending…" : "Send a new link"}
            </Button>
            {email && <p className="mt-3 text-xs text-muted">to {email}</p>}
          </>
        )}
        {resend.isError && (
          <InlineNote tone="critical" className="mt-3 justify-center">
            {resend.error instanceof ApiError && resend.error.status === 429
              ? resend.error.message
              : `The link could not be sent: ${(resend.error as Error).message}`}
          </InlineNote>
        )}
      </Frame>
    );
  }

  if (state === "refused") {
    return (
      <Frame title="This account cannot sign in">
        <p>{failure?.detail}</p>
        <BackToSignIn />
      </Frame>
    );
  }

  return (
    <Frame title="This link is not valid">
      <p>
        It may have been cut short when it was copied, or it belongs to an older
        message. Ask for a new one with your email address.
      </p>
      <Link to="/login" className={`${buttonClasses("primary")} mt-7 w-full`}>
        Back to sign in
      </Link>
    </Frame>
  );
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-5 py-16">
      <div className="w-full max-w-sm text-center">
        <Mark size={60} animate className="mx-auto" />
        <h1 className="mt-7 text-lg font-medium tracking-tight text-ink">{title}</h1>
        <div className="mt-3 text-sm leading-relaxed text-ink-2">{children}</div>
      </div>
    </div>
  );
}

function BackToSignIn() {
  return (
    <Link to="/login" className={`${buttonClasses("secondary")} mt-7 w-full`}>
      Back to sign in
    </Link>
  );
}
